import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Shell } from '../../../componentes/Shell.jsx';
import { formatearFecha, obtenerFechaDeNegocioHoy } from '../../../nucleo/fecha/fechaLocal.js';
import { BloqueDeMsiDeLaTarjeta } from '../../msi/componentes/BloqueDeMsiDeLaTarjeta.jsx';
import { DeudaDescompuestaDeLaTarjeta } from '../../deuda-descompuesta/componentes/DeudaDescompuestaDeLaTarjeta.jsx';
import { consultarFechasDeTarjeta, obtenerCuenta } from '../api/cuentasApi.js';
import { CorreccionesDelSaldoInicial } from '../componentes/CorreccionesDelSaldoInicial.jsx';
import { PRESENTACION_DE_LA_BAJA, resolverBajaDeCuenta } from '../logica/motivoDeBajaDeCuenta.js';
import { importe, NOMBRE_DE_MONEDA, vigencia, vigenciaVencida } from '../formato.js';
import AvisoDeError from '../../../nucleo/avisos/AvisoDeError.jsx';

const CLAVE_EFECTIVO = 'EFECTIVO';
/*
 * RN-752 (incremento 20/21) · Las dos claves que esta pantalla necesita preguntar
 * POR LISTA EXPLÍCITA. La bandera `esCredito` sigue sirviendo para lo que de verdad
 * dice —«es pasivo, su cifra es deuda»—, que vale igual en una tarjeta y en un
 * préstamo; lo que NO puede decidir es si se ofrece una superficie DE TARJETA.
 */
const CLAVE_CREDITO = 'CREDITO';
const CLAVE_PRESTAMO = 'PRESTAMO';

/**
 * Detalle de una cuenta o tarjeta.
 *
 * LECTURA ABIERTA: se ve la cuenta de la otra persona con su saldo y su detalle;
 * lo que no se puede es modificarla (RN-027, RN-028, RN-060). Quien implemente
 * esto filtrando también la lectura rompe D-01 (RN-067h).
 *
 * La acción "Renové mi plástico" se ofrece en Débito, Ahorro y Crédito, y NO en
 * Efectivo (D-28, RN-070g.ii). Ocultarla es ayuda visual: el servidor rechaza
 * igual la operación con RENOVACION_NO_APLICA (422).
 */
export default function DetalleDeCuenta() {
  const { idCuenta } = useParams();
  const [cuenta, setCuenta] = useState(null);
  const [fechas, setFechas] = useState(null);
  const [error, setError] = useState(null);
  const hoy = obtenerFechaDeNegocioHoy();
  // RN-547 · «Vuelve a intentarlo» del motivo CONSUMIDOR_NO_DISPONIBLE: se vuelve a
  // pedir la cuenta con sus saldos. Si esta vez llegan, el botón se habilita o
  // muestra el motivo real. No se asume cero por reintentar (RN-069f).
  const [intento, setIntento] = useState(0);
  const recargar = useCallback(() => setIntento((previo) => previo + 1), []);

  useEffect(() => {
    let vigente = true;
    obtenerCuenta(idCuenta)
      .then((datos) => {
        if (!vigente) return;
        setCuenta(datos);
        // RN-752 (incremento 20/21) · LISTA EXPLÍCITA DE TIPO: las fechas de corte
        // son de la TARJETA. Un préstamo también es `esCredito` —es pasivo— y NO
        // TIENE CORTE: pedírselas devolvería un rechazo del servidor (CA-20-19).
        if (datos.claveTipoCuenta === CLAVE_CREDITO) {
          // La fecha de referencia es la FECHA DE NEGOCIO del dispositivo: el
          // servidor no la suple (D-06, RF-013, RN-016).
          consultarFechasDeTarjeta(idCuenta, hoy).then(setFechas).catch(() => setFechas(null));
        }
      })
      .catch((fallo) => vigente && setError(fallo));
    return () => { vigente = false; };
  }, [idCuenta, hoy, intento]);

  if (error) {
    return (
      <Shell titulo="Cuenta" volverA="/cuentas">
        <AvisoDeError error={error} />
      </Shell>
    );
  }

  if (!cuenta) {
    return <Shell titulo="Cuenta" volverA="/cuentas"><div className="aviso i">Cargando…</div></Shell>;
  }

  const esEfectivo = cuenta.claveTipoCuenta === CLAVE_EFECTIVO;
  // RN-752 · lo que decide una superficie de TARJETA, por lista explícita.
  const esTarjeta = cuenta.claveTipoCuenta === CLAVE_CREDITO;
  const esPrestamo = cuenta.claveTipoCuenta === CLAVE_PRESTAMO;
  // Un préstamo se lista en la pestaña de Cuentas, no en la de Tarjetas.
  const volverA = esTarjeta ? '/cuentas/tarjetas' : '/cuentas';
  // La cifra de un pasivo es su DEUDA, y esto sí se decide con la bandera: vale
  // igual en una tarjeta y en un préstamo (D-24, D-434).
  const valor = cuenta.esCredito ? cuenta.deuda : cuenta.saldo;

  return (
    <Shell titulo={cuenta.nombre} subtitulo={`${cuenta.nombreTipoCuenta} · ${cuenta.codigoISO} · ${cuenta.esPropia ? 'tuya' : `de ${cuenta.nombrePropietario}`}${cuenta.activo ? '' : ' · desactivada'}`} volverA={volverA}>
      {!cuenta.activo && (
        <div className="aviso w">
          <b>Cuenta desactivada{cuenta.fechaBaja ? ` el ${formatearFecha(cuenta.fechaBaja.slice(0, 10))}` : ''}.</b> No
          se ofrece al capturar movimientos, pero <b>su historial se conserva íntegro</b> y sigue
          apareciendo en los reportes del periodo en que se usó. No se ve en la lista salvo
          que actives el control “Ver desactivadas”. Reactivarla es inmediata y no
          pide confirmación.
        </div>
      )}

      <div className="card">
        <div className="ayuda">{cuenta.esCredito ? 'Deuda actual · calculada' : 'Saldo actual · calculado'}</div>
        <div style={{ fontSize: 28, fontWeight: 700 }}>
          {importe(valor, cuenta.codigoISO)} <small style={{ fontSize: 13, color: 'var(--sec)', fontWeight: 400 }}>{cuenta.codigoISO}</small>
        </div>

        {/* El disponible y el límite son DE LA TARJETA: un préstamo no tiene línea
            de crédito, y sus dos datos viajan nulos (D-434, S-20-06). */}
        {esTarjeta && (
          <div className="ayuda">
            Disponible <b>{importe(cuenta.disponible, cuenta.codigoISO)}</b> de un límite de
            <b> {importe(cuenta.limiteCredito, cuenta.codigoISO)}</b>
            {cuenta.haySobregiro && <span className="chip"> ▲ Sobregiro</span>}
          </div>
        )}

        <div className="ayuda" style={{ marginTop: 6 }}>
          {cuenta.esCredito
            ? `Deuda inicial ${importe(cuenta.saldoInicial, cuenta.codigoISO)} + gastos − pagos`
            : `Saldo inicial ${importe(cuenta.saldoInicial, cuenta.codigoISO)} + ingresos − gastos ± traspasos`}
          {cuenta.cantidadMovimientos === 0 && ' (hoy, cero movimientos aportados)'}
        </div>

        {cuenta.puedeDesactivarse && <div className="chip" style={{ marginTop: 8 }}>✔ En cero · se puede desactivar</div>}
      </div>

      {/* LA DEUDA DESCOMPUESTA (incremento 18/21, RF-252, RN-699, RN-700): va
          INMEDIATAMENTE DEBAJO de la deuda total, porque lo primero que hay que
          decir es que esa cifra no cambia al separarla. Solo en tarjeta. */}
      <DeudaDescompuestaDeLaTarjeta cuenta={cuenta} />

      {!cuenta.esPropia && (
        <div className="aviso i">
          <b>Esta cuenta es de {cuenta.nombrePropietario}.</b> Puedes verla con su saldo y su detalle,
          pero <b>solo esa persona puede modificarla</b>. Sí puedes, en
          cambio, <b>{cuenta.esCredito ? 'pagarle esta tarjeta desde cualquiera de tus cuentas' : 'hacerle un traspaso desde una cuenta tuya'}</b>:
          el dinero sale de lo tuyo y puede entrar a lo de cualquiera.
        </div>
      )}

      <div className="card lista">
        <h3>Datos de la cuenta</h3>
        <Dato etiqueta="Tipo" valor={cuenta.nombreTipoCuenta} />
        <Dato etiqueta="Moneda" valor={`${NOMBRE_DE_MONEDA[cuenta.codigoISO] ?? ''} (${cuenta.codigoISO})`} />
        <Dato etiqueta="Propietario" valor={cuenta.esPropia ? 'Tuya' : cuenta.nombrePropietario} />
        {/* H-17 · El saldo inicial ya no se presenta nunca como «congelado»: se
            corrige siempre (RF-171, RN-533). La palabra «congelado» desaparece de
            este renglón porque dejó de ser verdad. */}
        <Dato
          etiqueta={cuenta.esCredito ? 'Deuda inicial' : 'Saldo inicial'}
          valor={`${importe(cuenta.saldoInicial, cuenta.codigoISO)} · corregible`}
        />
        {/* Incremento 18/21 · El dato tal cual, junto a la deuda de la que forma
            parte. El reparto completo está en el bloque de arriba. */}
        {esTarjeta && (
          <Dato
            etiqueta="De esa deuda, en compras a meses"
            valor={`${importe(cuenta.diferidoDeclarado, cuenta.codigoISO)} · corregible`}
          />
        )}
        {esEfectivo || esPrestamo ? (
          <Dato
            etiqueta="Datos del plástico"
            valor={esPrestamo ? 'no aplican en un préstamo' : 'no aplican en efectivo'}
          />
        ) : (
          <>
            <Dato etiqueta="Cuatro últimos dígitos" valor={cuenta.cuatroUltimosDigitos ? `•• ${cuenta.cuatroUltimosDigitos}` : 'no capturados'} />
            <Dato
              etiqueta="Vigencia del plástico"
              valor={vigencia(cuenta.vigenciaMes, cuenta.vigenciaAnio)
                ? `${vigencia(cuenta.vigenciaMes, cuenta.vigenciaAnio)}${vigenciaVencida(cuenta.vigenciaMes, cuenta.vigenciaAnio, hoy) ? ' · ya vencida · informativo' : ''}`
                : 'sin capturar'}
            />
          </>
        )}
        <Dato etiqueta="Notas" valor={cuenta.notas ?? '—'} />
        <Dato etiqueta="Movimientos que la referencian" valor={String(cuenta.cantidadMovimientos)} />
      </div>

      {/* [ELIMINADO 2026-10-03 · RF-154, D-252, RN-441] Aquí vivía un recuadro que
          explicaba por qué una cuenta de efectivo no tiene plástico, JUSTIFICABA
          el diseño de la pantalla —«no sería una opción inútil, sería una que no
          significa nada»— y CITABA EN PANTALLA el código de la API
          DATOS_DE_TARJETA_NO_APLICAN. En su lugar no queda nada: lo que la persona
          necesita saber ya está arriba, en el renglón «Datos del plástico · no
          aplican en efectivo», que se conserva sin cambio.
          LO QUE SE FUE ES LA EXPLICACIÓN, NO LA REGLA (RN-441a): en efectivo
          siguen sin mostrarse los cuatro últimos dígitos y la vigencia, y la
          acción «Renové mi plástico» sigue sin aparecer. */}

      {/* BLOQUE DEL 4/6 (RF-077.2): los planes de ESTA tarjeta y lo que cae en el
          corte próximo. La cifra es un REPARTO de la deuda ya mostrada arriba, NO
          deuda adicional (D-53, RN-103c). */}
      <BloqueDeMsiDeLaTarjeta cuenta={cuenta} />

      {esTarjeta && (
        <>
          <div className="card lista">
            <h3>Datos de la tarjeta</h3>
            <Dato etiqueta="Límite de crédito" valor={importe(cuenta.limiteCredito, cuenta.codigoISO)} />
            <Dato etiqueta="Día de corte" valor={`día ${cuenta.diaCorte}${fechas ? ` → ${formatearFecha(fechas.fechaCorteDelPeriodo)}` : ''}`} />
            <Dato etiqueta="Día límite de pago" valor={`día ${cuenta.diaLimitePago}${fechas ? ` → ${formatearFecha(fechas.fechaLimitePago)}` : ''}`} />
            {/* Los días de gracia NO vienen en CuentaDto (D-31): los da esta misma
                consulta de fechas, con la fecha de negocio del dispositivo. Si aún
                no respondió, no se inventa un número aproximado (RN-056b). */}
            <Dato etiqueta="Días de gracia" valor={fechas ? `${fechas.diasDeGracia} días · calculado` : 'calculando…'} />
          </div>

          <div className="aviso i">
            Los <b>días de gracia no se capturan</b>: se calculan con el día de corte y el día límite
            de pago, y por eso nunca pueden contradecirse. Las fechas se recorren
            solas al último día en los meses cortos.
          </div>
        </>
      )}

      {/* H-05 · LA SECCIÓN DEJA DE PROMETER Y NAVEGA (RF-168 a RF-170, RN-527).
          [RETIRADO 2026-10-06 · RN-441, RN-442] Aquí vivía «Próximamente verás aquí
          los movimientos de esta cuenta. Mientras tanto, los encuentras en
          Movimientos filtrando por esta cuenta.» Se retira COMPLETO y no se
          reemplaza por otro aviso ni por una explicación de por qué no hay lista
          embebida: en su lugar queda la acción que hace lo que la frase prometía.

          NO SE CONSTRUYE LISTA EMBEBIDA (RN-532): duplicaría el orden, la paginación,
          los cancelados y los totales del historial. La sección es una puerta, no una
          copia.

          LA ACCIÓN DICE QUÉ VA A MOSTRAR (RF-170): el historial abre con su filtro
          predeterminado —el mes en curso del dispositivo—, y el saldo de arriba es de
          toda la vida de la cuenta. Sin esta ayuda, las dos cifras parecerían
          contradecirse.

          SE OFRECE EN TODA CUENTA —propia o ajena, activa o desactivada— porque la
          lectura es abierta (RN-027, RN-530). */}
      <div className="card">
        <Link
          className="fila"
          to={`/movimientos?idCuenta=${cuenta.idCuenta}`}
          data-prueba="ver-movimientos-de-esta-cuenta"
        >
          <span className="crece">
            <b>Ver los movimientos de esta cuenta</b>
            <small>Se abren los de este mes; desde ahí puedes cambiar el periodo.</small>
          </span>
          <span aria-hidden="true">›</span>
        </Link>
      </div>

      {/* H-17 · El rastro de las correcciones, consultable. Solo se dibuja si hay al
          menos una (RF-176). */}
      <CorreccionesDelSaldoInicial cuenta={cuenta} />

      {/* El préstamo tiene su propia pantalla, con su plan de pagos, su calendario y
          sus pagos registrados (P-20-02): desde el detalle de la cuenta se ofrece la
          puerta, para que nadie se quede aquí buscándolos. */}
      {esPrestamo && (
        <div style={{ margin: 12 }}>
          <Link
            className="btn ancho"
            to={`/cuentas/prestamos/${cuenta.idCuenta}`}
            data-prueba="ver-el-prestamo"
          >
            Ver su plan de pagos y sus pagos
          </Link>
        </div>
      )}

      {cuenta.esPropia && cuenta.activo && (
        <div style={{ display: 'grid', gap: 8, margin: 12 }}>
          <Link className="btn" to={`/cuentas/${cuenta.idCuenta}/editar`}>
            Editar {esTarjeta ? 'tarjeta' : (esPrestamo ? 'préstamo' : 'cuenta')}
          </Link>

          {/* D-28, PRECISADO POR RN-752: la renovación existe en Débito, Ahorro y
              Crédito —LISTA EXPLÍCITA—; en Efectivo no, y en un PRÉSTAMO tampoco:
              no hay plástico que renovar y el servidor lo rechaza (CA-20-19). */}
          {!esEfectivo && !esPrestamo && (
            <Link className="btn sec" to={`/cuentas/${cuenta.idCuenta}/renovacion`}>
              {esTarjeta ? 'Renové mi tarjeta física' : `Renové mi plástico (tarjeta de ${cuenta.nombreTipoCuenta.toLowerCase()})`}
            </Link>
          )}

          {/* F-04 · EL BLOQUEO DE LA BAJA SE ANTICIPA (RF-181, RN-545 a RN-548b).
              Antes la acción se ofrecía SIEMPRE habilitada y el motivo aparecía en la
              pantalla siguiente: el error llegaba después de intentarlo, que es justo
              el defecto. Ahora se lee el par que YA viaja en el DTO —sin endpoint
              nuevo, sin consulta nueva y sin pedir el dato dos veces— y la acción se
              presenta deshabilitada con su motivo a la vista.

              DESHABILITAR NO ES EL CONTROL (RN-548a): el servidor rechaza la baja
              igual. Y la pantalla de baja CONSERVA su explicación completa: F-04
              ADELANTA el motivo, no lo muda de sitio. */}
          <AccionDeDesactivar cuenta={cuenta} alReintentar={recargar} />
        </div>
      )}

      {cuenta.esPropia && !cuenta.activo && (
        <div style={{ margin: 12 }}>
          <Link className="btn ancho" to={`/cuentas/${cuenta.idCuenta}/desactivar`}>Reactivar cuenta</Link>
        </div>
      )}
    </Shell>
  );
}

/**
 * F-04 · La acción «Desactivar» con su motivo ANTES de intentarla (RF-181, RF-182,
 * RN-545 a RN-548b).
 *
 * Los cuatro estados del par los resuelve `logica/motivoDeBajaDeCuenta.js`, que no
 * conoce esta pantalla y se puede probar solo. Aquí solo se pinta.
 *
 * Los TRES motivos están CERRADOS y ninguno es un código nuevo: CUENTA_CON_SALDO,
 * CONSUMIDOR_NO_DISPONIBLE y nulo. El enumerado de errores no crece en este
 * incremento (CA-11-20a).
 */
function AccionDeDesactivar({ cuenta, alReintentar }) {
  const baja = resolverBajaDeCuenta(cuenta);

  // RN-548 · Sin saldos la acción NO SE DIBUJA EN ABSOLUTO. Deshabilitarla con una
  // razón adivinada sería mentir, y habilitarla sería peor.
  if (baja.presentacion === PRESENTACION_DE_LA_BAJA.AUSENTE) return null;

  if (baja.presentacion === PRESENTACION_DE_LA_BAJA.HABILITADA) {
    return (
      <Link className="btn sec" to={`/cuentas/${cuenta.idCuenta}/desactivar`} data-prueba="desactivar-cuenta">
        Desactivar {cuenta.esCredito ? 'tarjeta' : 'cuenta'}
      </Link>
    );
  }

  return (
    <>
      <button
        type="button"
        className="btn sec"
        disabled
        aria-describedby="motivo-no-desactivable"
        data-prueba="desactivar-cuenta"
      >
        Desactivar {cuenta.esCredito ? 'tarjeta' : 'cuenta'}
      </button>

      <div className="aviso w" id="motivo-no-desactivable" data-prueba="motivo-no-desactivable">
        <b>{baja.texto}</b>
        {/* RN-546 · La salida se ofrece SIEMPRE, no solo en las cuentas sin
            movimientos: con H-17 la vía existe siempre, y ocultarla era ocultar la
            única salida. RN-546a · Pero el atajo no promete el cero cuando la cuenta
            tiene movimientos, porque corregir el saldo inicial cambia el saldo y rara
            vez lo deja en cero exacto. */}
        {baja.salida && <div>{baja.salida}</div>}

        {baja.rotuloDelAtajo && (
          <div style={{ marginTop: 8 }}>
            <Link
              to={`/cuentas/${cuenta.idCuenta}/editar${(cuenta.cantidadMovimientos ?? 0) === 0 ? '?cero=1' : ''}`}
              data-prueba="atajo-de-correccion"
            >
              {baja.rotuloDelAtajo} →
            </Link>
          </div>
        )}

        {baja.ofreceReintento && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn sec" onClick={alReintentar} data-prueba="reintentar-saldo">
              Volver a intentarlo
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/*
 * MISMO RENGLÓN Y MISMO DEFECTO QUE EN EL DETALLE DEL MOVIMIENTO (2026-08-31):
 * con un valor largo —el nombre completo de la otra persona, un saldo con su
 * código de divisa— la etiqueta se encogía y su texto se leía montado sobre el
 * valor. Se corrige igual, para no dejar viva la misma falla en la pantalla
 * gemela.
 */
function Dato({ etiqueta, valor }) {
  return (
    <div className="fila" style={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 2 }}>
      <span className="crece" style={{ flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>
        {etiqueta}
      </span>
      <b style={{ flex: '1 1 60%', minWidth: 0, textAlign: 'right', overflowWrap: 'anywhere' }}>
        {valor}
      </b>
    </div>
  );
}
